import { UseFormReturn } from "react-hook-form";
import { FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { IntakeFormData } from "./IntakeFormSchema";

interface InsuranceInfoSectionProps {
  form: UseFormReturn<IntakeFormData>;
}

export function InsuranceInfoSection({ form }: InsuranceInfoSectionProps) {
  const insuranceType = form.watch("insurance_type");
  const showMedicare = insuranceType === "medicare" || insuranceType === "dual_eligible";
  const showMedicaid = insuranceType === "medicaid" || insuranceType === "dual_eligible";

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-foreground border-b pb-2">Insurance Information</h3>

      <FormField
        control={form.control}
        name="insurance_type"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Insurance Type *</FormLabel>
            <Select onValueChange={field.onChange} defaultValue={field.value}>
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder="Select your insurance type" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem value="medicare">Medicare</SelectItem>
                <SelectItem value="medicaid">Medicaid</SelectItem>
                <SelectItem value="dual_eligible">Dual Eligible (Medicare + Medicaid)</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />

      {showMedicare && (
        <div className="space-y-4 p-4 bg-muted/50 rounded-lg">
          <FormField
            control={form.control}
            name="medicare_id"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Medicare ID Number</FormLabel>
                <FormControl>
                  <Input placeholder="1EG4-TE5-MK72" {...field} />
                </FormControl>
                <FormDescription>Your Medicare Beneficiary Identifier (MBI)</FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="space-y-2">
            <FormLabel>Medicare Coverage Parts</FormLabel>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <FormField
                control={form.control}
                name="medicare_part_a"
                render={({ field }) => (
                  <FormItem className="flex items-center space-x-2">
                    <FormControl>
                      <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                    <FormLabel className="!mt-0 font-normal">Part A (Hospital)</FormLabel>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="medicare_part_b"
                render={({ field }) => (
                  <FormItem className="flex items-center space-x-2">
                    <FormControl>
                      <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                    <FormLabel className="!mt-0 font-normal">Part B (Medical)</FormLabel>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="medicare_part_c"
                render={({ field }) => (
                  <FormItem className="flex items-center space-x-2">
                    <FormControl>
                      <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                    <FormLabel className="!mt-0 font-normal">Part C (Advantage)</FormLabel>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="medicare_part_d"
                render={({ field }) => (
                  <FormItem className="flex items-center space-x-2">
                    <FormControl>
                      <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                    <FormLabel className="!mt-0 font-normal">Part D (Prescription)</FormLabel>
                  </FormItem>
                )}
              />
            </div>
          </div>
        </div>
      )}

      {showMedicaid && (
        <div className="p-4 bg-muted/50 rounded-lg">
          <FormField
            control={form.control}
            name="medicaid_id"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Medicaid ID Number</FormLabel>
                <FormControl>
                  <Input placeholder="Your Medicaid ID" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      )}

      {/* Payer & Plan Information */}
      <div className="space-y-4">
        <h4 className="text-sm font-semibold text-foreground border-b pb-1">Payer & Plan Details</h4>
        <div className="grid md:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="payer_name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Payer Name</FormLabel>
                <FormControl>
                  <Input placeholder="Insurance company name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="group_number"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Group ID</FormLabel>
                <FormControl>
                  <Input placeholder="Group number" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="plan_type"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Plan Type</FormLabel>
                <FormControl>
                  <Input placeholder="HMO, PPO, EPO, etc." {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="effective_date"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Effective Date</FormLabel>
                <FormControl>
                  <Input placeholder="MM/DD/YYYY" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="edi_payer_id"
            render={({ field }) => (
              <FormItem>
                <FormLabel>EDI Payer ID</FormLabel>
                <FormControl>
                  <Input placeholder="EDI Payer ID" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="network_tier"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Network Tier</FormLabel>
                <FormControl>
                  <Input placeholder="In-network, Out-of-network, etc." {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="payer_phone"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Payer Phone</FormLabel>
                <FormControl>
                  <Input placeholder="Customer/Provider services phone" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="claims_address"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Claims Address</FormLabel>
                <FormControl>
                  <Input placeholder="Claims submission address" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="copay_amount"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Copay Amount</FormLabel>
                <FormControl>
                  <Input placeholder="e.g. $25 PCP / $50 Specialist" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="deductible_info"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Deductible Info</FormLabel>
                <FormControl>
                  <Input placeholder="e.g. $500 Individual / $1000 Family" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </div>

      {/* Pharmacy Benefits */}
      <div className="space-y-4">
        <h4 className="text-sm font-semibold text-foreground border-b pb-1">Pharmacy Benefits</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <FormField
            control={form.control}
            name="rx_bin"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Rx BIN</FormLabel>
                <FormControl>
                  <Input placeholder="BIN" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="rx_pcn"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Rx PCN</FormLabel>
                <FormControl>
                  <Input placeholder="PCN" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="rx_group"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Rx Group</FormLabel>
                <FormControl>
                  <Input placeholder="Group" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="rx_id"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Rx ID</FormLabel>
                <FormControl>
                  <Input placeholder="Rx ID" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </div>
    </div>
  );
}
